import {
  CHRONICLE_ORIGIN,
  chronicleDeviceStateAtom,
  chronicleSessionIdAtom,
  routesToChronicleAtom,
} from '@/atoms/chronicle';
import { USE_RECORDING_V2 } from '@/features/flags/legacy-flags';
import { useChroniclePauseRecording } from '@/hooks/desktop/useChroniclePauseRecording';
import { useChronicleResumeRecording } from '@/hooks/desktop/useChronicleResumeRecording';
import { useChronicleStartRecording } from '@/hooks/desktop/useChronicleStartRecording';
import { useChronicleStopRecording } from '@/hooks/desktop/useChronicleStopRecording';
import type { RecordingControlOptions } from '@/lib/telemetry/recordingControlTracking';
import {
  finishRecordingAttempt,
  setRecordingAttemptTransport,
  type RecordingStartOptions,
} from '@/lib/telemetry/recordingStartTracking';
import { createSessionFlowPreflightError } from '@/lib/telemetry/sessionFlowFailures';
import { isTauri } from '@/tauri/utils';
import { chronicleLogger } from '@/utils/logger/chronicle-logger';
import type { RecordingTransport } from '@heidi/capture/recording-commands';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtomValue } from 'jotai';
import { useRecordingV1 } from './useRecordingV1';
import { useRecordingV2 } from './useRecordingV2';

export type { RecordingTransport };

export const useRecording = () => {
  const { t } = useTranslations();
  const v1 = useRecordingV1();
  const v2 = useRecordingV2();
  const chronicleSessionId = useAtomValue(chronicleSessionIdAtom);
  const chronicleDeviceState = useAtomValue(chronicleDeviceStateAtom);
  const { startChronicleRecording } = useChronicleStartRecording();
  const { stopChronicleRecording } = useChronicleStopRecording();
  const { pauseChronicleRecording } = useChroniclePauseRecording();
  const { resumeChronicleRecording } = useChronicleResumeRecording();

  // Route by the active recording's transport, not the live input (see
  // routesToChronicleAtom).
  const routeToChronicle = useAtomValue(routesToChronicleAtom);

  if (isTauri() || !USE_RECORDING_V2()) {
    if (routeToChronicle) {
      return {
        ...v1,
        transport: 'chronicle' as RecordingTransport,
        startRecording: (options?: RecordingStartOptions) => {
          const tracking = options?.tracking;
          // A pre-existing device recording only blocks starting a NEW session;
          // with a bound session id the press is a resume of that recording
          // (recovered recordings keep recordingPreExisting=true).
          if (!chronicleSessionId && chronicleDeviceState.recordingPreExisting) {
            if (tracking) {
              setRecordingAttemptTransport(tracking, 'remote_device');
              finishRecordingAttempt(tracking, {
                outcome: 'failed',
                error: createSessionFlowPreflightError(
                  'DeviceInUseError',
                  'Chronicle has an unbound recording'
                ),
              });
            }
            toast({
              tone: 'destructive',
              title: t('settings.chronicle.notification.offlineRecordingInProgressTitle'),
              description: t(
                'settings.chronicle.notification.offlineRecordingInProgressDescription'
              ),
            });
            return;
          }

          // If a Chronicle session already exists, this is a resume from
          // paused state. Detect by checking chronicleSessionIdAtom — set on
          // start and cleared on stop — without changing the click's tracking.
          chronicleLogger.info('[remote] play pressed', {
            chronicleSessionId,
            branch: chronicleSessionId ? 'resume' : 'start',
          });
          if (chronicleSessionId) {
            void resumeChronicleRecording({
              origin: CHRONICLE_ORIGIN.DESKTOP,
              tracking,
            });
          } else {
            void startChronicleRecording({
              origin: CHRONICLE_ORIGIN.DESKTOP,
              tracking,
            });
          }
        },
        pauseRecording: (options?: RecordingControlOptions) => {
          void pauseChronicleRecording({
            origin: CHRONICLE_ORIGIN.DESKTOP,
            tracking: options?.tracking,
          });
        },
        stopRecording: (options?: RecordingControlOptions) => {
          void stopChronicleRecording({
            origin: CHRONICLE_ORIGIN.DESKTOP,
            tracking: options?.tracking,
          });
        },
      };
    }
    return { ...v1, transport: 'v1' as RecordingTransport };
  }
  return { ...v2, transport: 'v2' as RecordingTransport };
};
